import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import {
  Building2, ShoppingBag, Users, Hammer, Sparkles, Wind,
  CheckCircle, ArrowRight, ClipboardCheck, Play, ArrowRightCircle
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/services/")({
  component: Services,
  head: () => ({
    meta: [
      { title: "Services — Commercial Cleaning & Facility Solutions | Aureline" },
      { name: "description", content: "Explore detailed commercial cleaning, retail space janitorial, strata building common area maintenance, post-construction detailing, floor care, and specialty steam disinfection across BC." },
    ],
  }),
});

const serviceDetails = [
  {
    id: "commercial-office",
    n: "01",
    icon: Building2,
    title: "Commercial Office",
    desc: "Rotating janitorial programs tailored to modern workspaces, offices, conference rooms, and lobbies.",
    items: [
      "Desk & workspace sanitization",
      "Common area high-touch disinfection",
      "Waste & recycling management",
      "Restroom detailing & supply restocking"
    ],
    color: "mint"
  },
  {
    id: "retail-cleaning",
    n: "02",
    icon: ShoppingBag,
    title: "Retail Cleaning",
    desc: "Crisp, spotless presentation for retail outlets, showrooms, malls, and public shopping environments.",
    items: [
      "Storefront glass and entryway detailing",
      "High-traffic floor polishing & vacuuming",
      "Point-of-sale terminal sanitizing",
      "Fitting room cleaning & disinfection"
    ],
    color: "sky"
  },
  {
    id: "strata-common-areas",
    n: "03",
    icon: Users,
    title: "Strata & Common Areas",
    desc: "Keeping residential buildings, lobbies, elevators, and shared spaces safe, tidy, and clean.",
    items: [
      "Elevator track and wall detailing",
      "Common area corridor vacuuming",
      "Amenity room cleaning & reset",
      "Mailbox area & door handle sanitizing"
    ],
    color: "deep"
  },
  {
    id: "post-construction",
    n: "04",
    icon: Hammer,
    title: "Post-Construction",
    desc: "Detail-oriented dust containment and final clean-up to deliver properties turnover-ready.",
    items: [
      "Fine drywall dust mitigation",
      "Sticker & protective film removal",
      "Window frame & sill detailing",
      "Cabinet & appliance interior cleaning"
    ],
    color: "sky"
  },
  {
    id: "floor-surface-care",
    n: "05",
    icon: Sparkles,
    title: "Floor & Surface Care",
    desc: "Specialized hard-floor buffing, machine scrubbing, strip & wax, and deep surface restoration.",
    items: [
      "Viny Tile (VCT) strip and refinish",
      "Tile & grout machine scrubbing",
      "Carpet extraction and spot removal",
      "Concrete sealing & surface polish"
    ],
    color: "mint"
  },
  {
    id: "residential",
    n: "06",
    icon: Wind,
    title: "Residential",
    desc: "Premium residential deep cleaning and sanitization scheduled and confirmed via our client portal.",
    items: [
      "Whole-home deep sanitization & dusting",
      "Kitchen & washroom detailed sanitization",
      "Hardwood & carpet surface treatments",
      "Post-occupancy move-in/move-out detailing"
    ],
    color: "deep"
  }
];

const steps = [
  { step: "01", title: "Space Assessment", desc: "We review your facility footprint, surface types, and high-traffic zones." },
  { step: "02", title: "Checklist Engineering", desc: "We draft a site-specific checklist corresponding to exact spatial zones." },
  { step: "03", title: "Supervisor Walkthrough", desc: "Crews are onboarded and audited on-site by a designated supervisor." },
  { step: "04", title: "Verified Service Launch", desc: "Visits go live with digital reports and quality inspection logs sent directly to you." }
];

function Services() {
  return (
    <Layout>
      {/* HERO */}
      <section className="bg-gradient-to-b from-secondary/40 via-secondary/10 to-background py-20 border-b border-border/40">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-navy eyebrow animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-sky" /> Our Services
            </div>
            <h1 className="mt-6 font-display text-4xl md:text-6xl text-navy leading-[1.2] tracking-tight animate-fade-up delay-100">
              Elevated cleaning <br />
              <span className="italic text-navy-soft">custom-fit</span> for your property.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-fade-up delay-200">
              From routine janitorial contracts to post-construction detail and specialty floor maintenance, 
              every program is built around your specific facility requirements.
            </p>
          </div>
        </div>
      </section>

      {/* DETAILED SERVICES GRID */}
      <section className="py-24">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceDetails.map((s) => (
              <article
                key={s.title}
                className={`bg-card rounded-2xl p-8 xl:p-10 shadow-card border border-border/60 flex flex-col justify-between hover:shadow-elevated hover-lift transition border-t-4 ${
                  s.color === "mint" ? "border-t-mint hover:border-sky/40" :
                  s.color === "sky" ? "border-t-sky hover:border-mint/40" :
                  "border-t-navy-soft hover:border-sky/40"
                }`}
              >
                <div className="flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                        s.color === "mint" ? "bg-mint/30 text-mint-ink" :
                        s.color === "sky" ? "bg-sky/30 text-navy-soft" :
                        "bg-secondary/70 text-navy-deep"
                      }`}>
                        <s.icon className="w-6 h-6" />
                      </div>
                      <span className="font-display text-base text-muted-foreground">{s.n}</span>
                    </div>
                    <h2 className="mt-8 font-display text-2xl text-navy">{s.title}</h2>
                    <p className="mt-4 text-sm xl:text-base text-muted-foreground leading-relaxed">{s.desc}</p>
                    
                    <div className="mt-8 border-t border-border/60 pt-6">
                      <div className="text-xs uppercase tracking-wider text-navy font-semibold mb-4">What's Included:</div>
                      <ul className="space-y-3">
                        {s.items.map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                            <CheckCircle className={`w-4.5 h-4.5 flex-shrink-0 mt-0.5 ${
                              s.color === "mint" ? "text-mint-ink" :
                              s.color === "sky" ? "text-navy-soft" :
                              "text-navy-deep"
                            }`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8 pt-4 border-t border-border/40">
                    <Link
                      to="/services/$serviceId"
                      params={{ serviceId: s.id }}
                      className="inline-flex items-center gap-2 text-sm text-navy-soft font-semibold hover:underline"
                    >
                      Learn more about {s.title} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK / ONBOARDING */}
      <section className="py-24 gradient-navy-blue text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/2 stripe-accent-fade-left" aria-hidden />
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <div className="eyebrow text-mint">Onboarding Framework</div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight">
              How we launch your facility plan.
            </h2>
            <p className="mt-4 text-primary-foreground/75 xl:text-lg">
              We implement a structured onboarding process to make transitioning your janitorial provider 
              smooth, transparent, and hassle-free.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((st) => (
              <div key={st.step} className="bg-primary-foreground/5 rounded-2xl p-8 border border-primary-foreground/10 flex flex-col justify-between hover:border-sky/30 transition-colors duration-300">
                <div>
                  <span className="font-display text-5xl text-sky/40 block mb-6">{st.step}</span>
                  <h3 className="font-display text-xl text-primary-foreground mb-3">{st.title}</h3>
                  <p className="text-sm text-primary-foreground/80 leading-relaxed">{st.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-cream">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <ClipboardCheck className="w-12 h-12 text-mint mx-auto" />
            <h2 className="mt-6 font-display text-4xl md:text-5xl text-navy tracking-tight">
              Ready to design a custom checklist for your facility?
            </h2>
            <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
              Contact us today for a site consultation. We'll outline your clean zones, 
              determine frequencies, and deliver a practical, transparent quote.
            </p>
            <div className="mt-10 flex justify-center">
              <Link to="/" hash="contact">
                <Button className="rounded-full h-12 px-8 bg-navy text-primary-foreground hover:bg-navy/90 text-base">
                  Get a customized quote <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
